import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import React, { useEffect, useState } from "react";
import { AdvancedTableSliceFragment } from "../../types/graphql";
import { htmlSerializer } from "../../utils/htmlSerializer";
import {
  TableBase,
  Heading,
  Title,
  TableWrapper,
  StyledTable,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableEntry,
  MobileHeader,
  EntryValue,
  Caption,
} from "./advanced-table.styled";
import { isUrl } from "../../utils/url-detector";
import Papa from "papaparse";
import { Slice } from "@prismicio/types";
import { IPageContext } from "../../types/types";
import { SharedSliceType } from "../shared-slice/shared-slice";

interface IPureAdvancedTableProps {
  cellRows: string[][];
  data: AdvancedTableSliceFragment;
}

export const PureAdvancedTable = (props: IPureAdvancedTableProps) => {
  const { cellRows, data } = props;
  const tableData = data.primary;
  const headerRow = cellRows?.shift();
  const numColumns = headerRow?.length;

  return (
    (cellRows.length > 0 && (
      <TableBase>
        {tableData?.heading && <Heading>{tableData.heading}</Heading>}
        {tableData?.table_title && <Title>{tableData.table_title}</Title>}
        <TableWrapper>
          <StyledTable>
            <TableHead>
              <TableRow>
                {headerRow &&
                  // Only output table headers for defined headers
                  headerRow.map((header) => {
                    return <TableHeader key={header}>{header}</TableHeader>;
                  })}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Render row with entries */}
              {cellRows?.map((cellRow, rowIndex) => {
                if (!cellRow || !numColumns) {
                  return;
                }

                // Create array of entries
                const entries = [];
                for (
                  let entryIndex = 0;
                  entryIndex < numColumns;
                  entryIndex++
                ) {
                  const entryValue = cellRow[entryIndex];

                  // if the entry is a URL, attempt to render as an image
                  const isEntryURL = isUrl(entryValue);
                  entries.push(
                    <TableEntry key={entryIndex}>
                      {cellRow[entryIndex] && (
                        <>
                          <MobileHeader>{headerRow[entryIndex]}</MobileHeader>
                          <EntryValue>
                            {isEntryURL ? (
                              <img src={cellRow[entryIndex]} />
                            ) : (
                              cellRow[entryIndex]
                            )}
                          </EntryValue>
                        </>
                      )}
                    </TableEntry>
                  );
                }
                return <TableRow key={rowIndex}>{entries}</TableRow>;
              })}
            </TableBody>
          </StyledTable>
        </TableWrapper>
        {tableData?.table_caption && (
          <Caption>
            <PrismicRichText
              field={tableData.table_caption.richText}
              components={htmlSerializer}
            />
          </Caption>
        )}
      </TableBase>
    )) || <></>
  );
};

export type IAdvancedTableProps = Slice<
  SharedSliceType,
  AdvancedTableSliceFragment["primary"]
> & { type: SharedSliceType.advanced_table };

export const AdvancedTable = ({
  slice,
  context,
}: SliceComponentProps<IAdvancedTableProps, IPageContext>) => {
  const table = slice.primary?.csv_file?.localFile;
  const tableFileMediaType = table?.internal?.mediaType;
  const tableData = table?.internal?.content;
  const [previewData, setPreviewData] = useState<string>("");
  const [shouldRenderPreview, setShouldRenderPreview] =
    useState<boolean>(false);

  let cellRows;

  const hasCSVExtention = (url: string) => {
    return url.match(/\.csv$/); // Checks if url ends with csv
  };

  useEffect(() => {
    if (
      table &&
      table.publicURL &&
      shouldRenderPreview &&
      hasCSVExtention(table.publicURL)
    ) {
      fetch(table.publicURL)
        .then((res) => {
          if (res.ok && res.headers.get("content-type") == "text/csv") {
            return res.text();
          }
          throw "Invalid content type for advanced table";
        })
        .then((data) => {
          setPreviewData(data);
        })
        .catch((e) => console.log(e));
    }
  }, [shouldRenderPreview]);

  if (tableData && tableFileMediaType === "text/csv") {
    cellRows = Papa.parse<string[]>(tableData);
  } else if (table && table.publicURL && shouldRenderPreview === false) {
    // When in preview mode only the public url is available
    // while the entire `internal` propery will be missing.
    // We check for the existence of this situation to determine
    // if we are in preview mode.
    setShouldRenderPreview(true);
  }
  if (previewData) {
    cellRows = Papa.parse<string[]>(previewData);
  }
  return <PureAdvancedTable cellRows={cellRows?.data || [[]]} data={slice} />;
};
